import { Analytics } from '@vercel/analytics/react';
import { useState, useRef, useEffect } from "react";

const TREES_DEFAULT = [
  { x: 6,  y: 62, scale: 1.15, opacity: 0.55 },
  { x: 13, y: 70, scale: 0.75, opacity: 0.38 },
  { x: 4,  y: 75, scale: 0.55, opacity: 0.28 },
  { x: 88, y: 58, scale: 1.2,  opacity: 0.52 },
  { x: 93, y: 67, scale: 0.8,  opacity: 0.35 },
  { x: 96, y: 74, scale: 0.6,  opacity: 0.25 },
  { x: 78, y: 72, scale: 0.65, opacity: 0.32 },
  { x: 20, y: 76, scale: 0.5,  opacity: 0.22 },
  { x: 82, y: 78, scale: 0.45, opacity: 0.20 },
];

const TREES_EXTRA = [
  { x: 30, y: 74, scale: 0.7,  opacity: 0.30 },
  { x: 38, y: 78, scale: 0.5,  opacity: 0.22 },
  { x: 50, y: 72, scale: 0.85, opacity: 0.35 },
  { x: 60, y: 76, scale: 0.6,  opacity: 0.25 },
  { x: 70, y: 73, scale: 0.9,  opacity: 0.38 },
  { x: 24, y: 80, scale: 0.45, opacity: 0.18 },
  { x: 44, y: 80, scale: 0.4,  opacity: 0.18 },
  { x: 56, y: 79, scale: 0.5,  opacity: 0.20 },
  { x: 66, y: 80, scale: 0.42, opacity: 0.17 },
  { x: 10, y: 68, scale: 0.6,  opacity: 0.28 },
  { x: 91, y: 62, scale: 0.55, opacity: 0.25 },
];

const MUTED_COLORS = ["#5a7a4a","#4a6e3a","#6a8a5a","#527045","#638555","#4e7040"];
const VIVID_COLORS = ["#2d9e4f","#38b55a","#22c45e","#4ab848","#1db954","#30a84d","#56c462","#28b04e","#3dcc60","#48d46a","#1fa84a","#35c058"];

const BUTTERFLIES_DEFAULT = [
  { left:"30%", top:"28%", drift:"drift1", color:"#8b7355", vividColor:"#ff6b9d", flapDelay:0.0  },
  { left:"62%", top:"22%", drift:"drift2", color:"#7a8b55", vividColor:"#a29bfe", flapDelay:0.2  },
  { left:"48%", top:"40%", drift:"drift3", color:"#a08060", vividColor:"#fdcb6e", flapDelay:0.35 },
];

const BUTTERFLIES_EXTRA = [
  { left:"18%", top:"32%", drift:"drift2", color:"#9b8566", vividColor:"#fd79a8", flapDelay:0.1  },
  { left:"72%", top:"35%", drift:"drift3", color:"#7a7055", vividColor:"#00cec9", flapDelay:0.25 },
  { left:"55%", top:"18%", drift:"drift1", color:"#8a7a60", vividColor:"#55efc4", flapDelay:0.4  },
  { left:"38%", top:"50%", drift:"drift3", color:"#907060", vividColor:"#e17055", flapDelay:0.15 },
  { left:"82%", top:"44%", drift:"drift2", color:"#7a8060", vividColor:"#74b9ff", flapDelay:0.3  },
  { left:"10%", top:"45%", drift:"drift1", color:"#857565", vividColor:"#fab1a0", flapDelay:0.05 },
];

const COPY = {
  EN: {
    cities:       <>Berlin &nbsp;·&nbsp; Munich &nbsp;·&nbsp; Frankfurt &nbsp;·&nbsp; <span style={{opacity:0.55,fontStyle:"italic"}}>more cities coming</span></>,
    citiesMobile: "Berlin · Munich · Frankfurt",
    moreCity:     "more cities coming",
    tagline:      <>The city is fast.<br/><em>Find a place to slow down.</em></>,
    taglineAfter: <><em>A seat is waiting for you.</em><br/>See you out there.</>,
    cityPlaceholder: "your city",
    placeholder:  "your@email.com",
    button:       "Join the waitlist",
    confirm:      "We'll be in touch. ✦",
    stat:         "city benches mapped",
    ios:          "Coming to iOS",
  },
  DE: {
    cities:       <>Berlin &nbsp;·&nbsp; München &nbsp;·&nbsp; Frankfurt &nbsp;·&nbsp; <span style={{opacity:0.55,fontStyle:"italic"}}>weitere Städte folgen</span></>,
    citiesMobile: "Berlin · München · Frankfurt",
    moreCity:     "weitere Städte folgen",
    tagline:      <>Die Stadt ist schnell.<br/><em>Find einen Platz zum Durchatmen.</em></>,
    taglineAfter: <><em>Ein Platz wartet auf dich.</em><br/>Bis bald draußen.</>,
    cityPlaceholder: "deine Stadt",
    placeholder:  "deine@email.de",
    button:       "Auf die Warteliste",
    confirm:      "Wir melden uns. ✦",
    stat:         "Stadtbänke kartiert",
    ios:          "Kommt für iOS",
  },
};

function easeOutExpo(t) {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

function useCounter(target, duration = 2400, delay = 800) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = null, raf;
    const timeout = setTimeout(() => {
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / duration, 1);
        setCount(Math.floor(easeOutExpo(p) * target));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(timeout); cancelAnimationFrame(raf); };
  }, [target, duration, delay]);
  return count;
}

function useHeadMeta(submitted) {
  useEffect(() => {
    document.title = "lets.bench.it";
    const bgColor = submitted ? "#e8f5e1" : "#f5f0e8";
    document.documentElement.style.background = bgColor;
    document.body.style.background = bgColor;
    let tc = document.querySelector("meta[name='theme-color']");
    if (!tc) { tc = document.createElement("meta"); tc.name = "theme-color"; document.head.appendChild(tc); }
    tc.content = bgColor;

    // Critical: ensure viewport is set correctly
    let vp = document.querySelector("meta[name='viewport']");
    if (!vp) {
      vp = document.createElement("meta");
      vp.name = "viewport";
      document.head.appendChild(vp);
    }
    vp.setAttribute("content", "width=device-width, initial-scale=1.0");

    const canvas = document.createElement("canvas");
    canvas.width = 32; canvas.height = 32;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#f5f0e8";
    ctx.beginPath(); ctx.arc(16,16,16,0,Math.PI*2); ctx.fill();
    ctx.fillStyle = "#3d2f1e";
    ctx.beginPath(); ctx.roundRect(4,13,24,4,2); ctx.fill();
    ctx.globalAlpha = 0.7;
    ctx.beginPath(); ctx.roundRect(5,8,22,3,1.5); ctx.fill();
    ctx.globalAlpha = 0.85;
    ctx.beginPath(); ctx.roundRect(7,17,3,7,1); ctx.fill();
    ctx.beginPath(); ctx.roundRect(22,17,3,7,1); ctx.fill();
    const url = canvas.toDataURL("image/png");
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
    link.href = url;
  }, [submitted]);
}

function BenchSVG({ size = 16, color = "#f5f0e8", wiggle = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{display:"block", animation: wiggle ? "benchWiggle 0.5s ease infinite" : "none"}}>
      <rect x="3" y="10" width="18" height="3" rx="1.5" fill={color}/>
      <rect x="4" y="6.5" width="16" height="2.5" rx="1.25" fill={color} opacity="0.75"/>
      <rect x="5"  y="13" width="2" height="5.5" rx="1" fill={color} opacity="0.85"/>
      <rect x="17" y="13" width="2" height="5.5" rx="1" fill={color} opacity="0.85"/>
    </svg>
  );
}

function AppleIcon({ size = 15, color }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
}

function Tree({ scale=1, opacity=0.5, color="#5a7a4a", vivid=false, isExtra=false, extraDelay=0 }) {
  const w = 60*scale, h = 110*scale;
  return (
    <svg width={w} height={h} viewBox="0 0 60 110" style={{
      opacity, display:"block",
      filter: vivid ? `drop-shadow(0 4px 20px ${color}55)` : "none",
      transition:`opacity ${isExtra?0.8:1.2}s ease ${extraDelay}s, filter 1.2s ease ${extraDelay}s`,
    }}>
      <rect x="26" y="82" width="8" height="26" rx="3" fill={color} opacity={vivid?0.85:0.55} style={{transition:`fill 1.2s ease ${extraDelay}s`}}/>
      <ellipse cx="30" cy="72" rx="22" ry="18" fill={color} opacity={vivid?0.55:0.35} style={{transition:`fill 1.2s ease ${extraDelay}s`}}/>
      <ellipse cx="30" cy="56" rx="18" ry="16" fill={color} opacity={vivid?0.72:0.5}  style={{transition:`fill 1.2s ease ${extraDelay}s`}}/>
      <ellipse cx="30" cy="40" rx="14" ry="13" fill={color} opacity={vivid?0.88:0.6}  style={{transition:`fill 1.2s ease ${extraDelay}s`}}/>
      <ellipse cx="30" cy="27" rx="9"  ry="9"  fill={color} opacity={vivid?1:0.7}     style={{transition:`fill 1.2s ease ${extraDelay}s`}}/>
      {vivid && <ellipse cx="25" cy="34" rx="3" ry="2.5" fill="white" opacity="0.16"/>}
    </svg>
  );
}

function Butterfly({ left, top, drift, color, vividColor, flapDelay, vivid, extraDelay=0 }) {
  const c = vivid ? vividColor : color;
  const dur = drift==="drift1" ? 9 : drift==="drift2" ? 12 : 10;
  return (
    <div style={{position:"absolute", left, top, animation:`${drift} ${dur}s ease-in-out infinite`, animationDelay:`${flapDelay}s`}}>
      <svg width="22" height="16" viewBox="0 0 22 16" style={{display:"block"}}>
        <g style={{transformOrigin:"11px 8px", animation:`flap 0.7s ease-in-out infinite`, animationDelay:`${flapDelay}s`}}>
          <ellipse cx="5.5" cy="6"  rx="5.5" ry="5"   fill={c} opacity={vivid?0.88:0.55} style={{transition:`fill 0.9s ease ${extraDelay}s`}}/>
          <ellipse cx="5"   cy="11" rx="4"   ry="3.5" fill={c} opacity={vivid?0.72:0.4}  style={{transition:`fill 0.9s ease ${extraDelay}s`}}/>
        </g>
        <g style={{transformOrigin:"11px 8px", animation:`flap 0.7s ease-in-out infinite`, animationDelay:`${flapDelay}s`, transform:"scaleX(-1) translateX(-22px)"}}>
          <ellipse cx="5.5" cy="6"  rx="5.5" ry="5"   fill={c} opacity={vivid?0.88:0.55} style={{transition:`fill 0.9s ease ${extraDelay}s`}}/>
          <ellipse cx="5"   cy="11" rx="4"   ry="3.5" fill={c} opacity={vivid?0.72:0.4}  style={{transition:`fill 0.9s ease ${extraDelay}s`}}/>
        </g>
        <ellipse cx="11" cy="8" rx="1.2" ry="5" fill={c} opacity={vivid?1:0.7} style={{transition:`fill 0.9s ease ${extraDelay}s`}}/>
      </svg>
    </div>
  );
}

function XIcon({ size=15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/>
    </svg>
  );
}

function useWindowWidth() {
  const [w, setW] = useState(0);
  useEffect(() => {
    setW(window.innerWidth);
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

export default function BenchLanding() {
  const [mouse, setMouse]         = useState({ x:0.5, y:0.5 });
  const [city, setCity]           = useState("");
  const [email, setEmail]         = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [lang, setLang]           = useState("EN");
  const [btnHover, setBtnHover]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const containerRef = useRef(null);
  const width    = useWindowWidth();
  const isMobile = width > 0 && width < 640;
  const count    = useCounter(13247, 2400, 800);

  useHeadMeta(submitted);
  const t = COPY[lang];

  const handleMouseMove = (e) => {
    if (!containerRef.current || isMobile) return;
    const rect = containerRef.current.getBoundingClientRect();
    setMouse({ x:(e.clientX-rect.left)/rect.width, y:(e.clientY-rect.top)/rect.height });
  };

  const handleSubmit = async () => {
    if (!email) return;
    setSubmitted(true);
    try {
      await fetch('/api/subscribe', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email, city }),
      });
    } catch {}
  };

  const bx  = isMobile ? 0 : (mouse.x-0.5)*-8;
  const by  = isMobile ? 0 : (mouse.y-0.5)*-5;
  const fmt = (n) => lang==="DE" ? n.toLocaleString("de-DE") : n.toLocaleString("en-GB");

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onClick={() => menuOpen && setMenuOpen(false)}
      style={{
        background: submitted ? "#e8f5e1" : "#f5f0e8",
        minHeight:"100vh", width:"100%", overflow:"hidden", position:"relative",
        transition:"background 1.8s ease",
        ...(isMobile ? { height:"100vh", maxHeight:"100vh", position:"fixed", top:0, left:0, right:0, bottom:0 } : {}),
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;1,300;1,400&family=Geist:wght@300;400&display=swap');
        *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
        html, body { overflow:hidden; height:100%; background:#f5f0e8; }
        html { background:#f5f0e8; }

        @keyframes fadeUp      { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeIn      { from{opacity:0} to{opacity:1} }
        @keyframes treeRise    { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
        @keyframes extraTreeIn { from{opacity:0;transform:translateY(22px) scale(0.9)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes titlePop    { 0%{transform:scale(1)} 40%{transform:scale(1.05)} 100%{transform:scale(1)} }
        @keyframes menuSlide   { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes benchWiggle {
          0%,100%{transform:translateY(0) rotate(0deg)}
          25%{transform:translateY(-2px) rotate(-5deg)}
          75%{transform:translateY(-1px) rotate(4deg)}
        }
        @keyframes flap { 0%,100%{transform:scaleX(1)} 50%{transform:scaleX(0.18)} }
        @keyframes drift1 {
          0%{transform:translate(0,0) rotate(-4deg)} 25%{transform:translate(18px,-14px) rotate(3deg)}
          50%{transform:translate(38px,-5px) rotate(-2deg)} 75%{transform:translate(22px,-20px) rotate(4deg)}
          100%{transform:translate(0,0) rotate(-4deg)}
        }
        @keyframes drift2 {
          0%{transform:translate(0,0) rotate(3deg)} 30%{transform:translate(-20px,-18px) rotate(-4deg)}
          60%{transform:translate(-10px,-28px) rotate(2deg)} 100%{transform:translate(0,0) rotate(3deg)}
        }
        @keyframes drift3 {
          0%{transform:translate(0,0)} 20%{transform:translate(12px,-22px) rotate(-3deg)}
          55%{transform:translate(28px,-10px) rotate(3deg)} 80%{transform:translate(8px,-30px) rotate(-2deg)}
          100%{transform:translate(0,0)}
        }

        .fade-up  { opacity:0; animation:fadeUp 1s cubic-bezier(0.16,1,0.3,1) forwards; }
        .fade-in  { opacity:0; animation:fadeIn 1.2s ease forwards; }
        .tree-rise{ animation:treeRise 1.4s cubic-bezier(0.16,1,0.3,1) forwards; }

        input[type="text"], input[type="email"] {
          font-family:'Geist',sans-serif;
          font-size:16px;
          font-weight:300; letter-spacing:0.02em;
          background:transparent; border:none;
          border-bottom:1px solid rgba(100,80,55,0.3);
          color:#3d2f1e; outline:none; padding:14px 0; width:100%;
          transition:border-color 0.3s;
          -webkit-appearance:none; border-radius:0;
        }
        input[type="text"]::placeholder, input[type="email"]::placeholder { color:rgba(100,80,55,0.35); }
        input[type="text"]:focus, input[type="email"]:focus { border-bottom-color:rgba(100,80,55,0.75); }

        .label {
          font-family:'Geist',sans-serif; font-weight:300;
          letter-spacing:0.16em; text-transform:uppercase; transition:color 1.2s ease;
        }
        .x-link {
          display:flex; align-items:center; gap:8px;
          font-family:'Geist',sans-serif; font-weight:300;
          letter-spacing:0.08em; text-transform:uppercase;
          color:rgba(60,45,28,0.5); text-decoration:none; transition:color 0.25s ease;
          -webkit-tap-highlight-color:transparent;
        }
        .x-link:hover, .x-link:active { color:rgba(60,45,28,0.9); }
        .ios-pill {
          display:inline-flex; align-items:center; gap:8px;
          border:1px solid rgba(100,80,55,0.22); border-radius:999px;
          font-family:'Geist',sans-serif; font-weight:300;
          letter-spacing:0.1em; text-transform:uppercase;
          color:rgba(60,45,28,0.5);
          background:rgba(245,240,232,0.6); backdrop-filter:blur(8px);
          transition:border-color 0.25s, color 0.25s, background 0.25s; cursor:default;
        }
        .ios-pill.vivid {
          border-color:rgba(30,140,60,0.25); color:rgba(30,100,50,0.55);
          background:rgba(232,245,225,0.6);
        }
      `}</style>

      {/* ── TOP BAR ── */}
      <div style={{
        position:"fixed", top:0, left:0, right:0, zIndex:50,
        display:"grid", gridTemplateColumns:"1fr auto 1fr", alignItems:"center",
        padding: isMobile ? "0 20px" : "16px 20px",
        height: isMobile ? 60 : "auto",
        pointerEvents:"none",
      }}>

        {/* Language switcher */}
        <div style={{display:"flex", alignItems:"center", gap:0, pointerEvents:"all"}}>
          {["EN","DE"].map((l,i) => (
            <span key={l} style={{display:"flex", alignItems:"center"}}>
              {i>0 && <span style={{color:"rgba(60,45,28,0.2)", fontSize:11, padding:"0 4px"}}>·</span>}
              <button
                onClick={() => setLang(l)}
                style={{
                  fontFamily:"'Geist',sans-serif",
                  fontSize: isMobile ? 13 : 10,
                  fontWeight: lang===l ? 400 : 300,
                  letterSpacing:"0.14em", textTransform:"uppercase",
                  background:"none", border:"none", cursor:"pointer",
                  padding: isMobile ? "8px 4px" : "4px 2px",
                  minHeight: isMobile ? 44 : "auto",
                  minWidth: isMobile ? 36 : "auto",
                  color: lang===l ? "rgba(60,45,28,0.85)" : "rgba(60,45,28,0.3)",
                  WebkitTapHighlightColor:"transparent",
                  display:"flex", alignItems:"center", justifyContent:"center",
                }}
              >{l}</button>
            </span>
          ))}
        </div>

        {/* Desktop centre */}
        {!isMobile ? (
          <div style={{
            display:"flex", alignItems:"center", gap:24,
            justifyContent:"center", pointerEvents:"all",
          }}>
            <a href="/about.html" className="x-link" style={{fontSize:11}}>about</a>
            <a href="/privacy-policy.html" className="x-link" style={{fontSize:11}}>privacy</a>
          </div>
        ) : <div/>}

        {/* Right */}
        <div
          style={{display:"flex", alignItems:"center", justifyContent:"flex-end", pointerEvents:"all", position:"relative"}}
          onClick={e => e.stopPropagation()}
        >
          {/* Desktop X links */}
          {!isMobile && (
            <div style={{display:"flex", alignItems:"center", gap:20}}>
              <a href="https://x.com/gab_cerdan" target="_blank" rel="noopener noreferrer" className="x-link" style={{fontSize:11}}>
                <XIcon size={13}/> @gab_cerdan
              </a>
              <a href="https://x.com/bleyen_louis" target="_blank" rel="noopener noreferrer" className="x-link" style={{fontSize:11}}>
                <XIcon size={13}/> @bleyen_louis
              </a>
            </div>
          )}

          {/* Mobile hamburger */}
          {isMobile && (
            <button
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Menu"
              style={{
                background:"none", border:"none", cursor:"pointer",
                width:44, height:44,
                display:"flex", flexDirection:"column",
                alignItems:"center", justifyContent:"center", gap:5,
                WebkitTapHighlightColor:"transparent",
              }}
            >
              <span style={{display:"block", width:22, height:1.5, background:"rgba(60,45,28,0.65)", transition:"all 0.25s", transform: menuOpen?"rotate(45deg) translate(4.5px,4.5px)":"none"}}/>
              <span style={{display:"block", width:22, height:1.5, background:"rgba(60,45,28,0.65)", transition:"all 0.25s", opacity: menuOpen?0:1}}/>
              <span style={{display:"block", width:22, height:1.5, background:"rgba(60,45,28,0.65)", transition:"all 0.25s", transform: menuOpen?"rotate(-45deg) translate(4.5px,-4.5px)":"none"}}/>
            </button>
          )}

          {/* Mobile dropdown */}
          {isMobile && menuOpen && (
            <div style={{
              position:"fixed", top:68, right:20,
              background: submitted ? "rgba(234,246,228,0.97)" : "rgba(249,245,239,0.97)",
              backdropFilter:"blur(20px)",
              border:"1px solid rgba(100,80,55,0.1)",
              borderRadius:12, overflow:"hidden",
              minWidth:200,
              boxShadow:"0 8px 40px rgba(60,45,28,0.13)",
              animation:"menuSlide 0.2s ease forwards",
            }}>
              {[
                {label:"about",         href:"/about.html",                icon:null},
                {label:"privacy",       href:"/privacy-policy.html",       icon:null},
                {label:"@gab_cerdan",   href:"https://x.com/gab_cerdan",   icon:<XIcon size={13}/>},
                {label:"@bleyen_louis", href:"https://x.com/bleyen_louis", icon:<XIcon size={13}/>},
              ].map(({label,href,icon},i) => (
                <a key={label} href={href}
                  target={href.startsWith("http")?"_blank":undefined}
                  rel={href.startsWith("http")?"noopener noreferrer":undefined}
                  className="x-link"
                  onClick={() => setMenuOpen(false)}
                  style={{
                    padding:"16px 22px", fontSize:13,
                    borderBottom: i < 3 ? "1px solid rgba(100,80,55,0.07)" : "none",
                  }}
                >
                  {icon}{label}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── BACKGROUND ── */}
      <div style={{position:"absolute", inset:0, pointerEvents:"none"}}>
        <svg style={{position:"absolute", inset:0, width:"100%", height:"100%", opacity:submitted?0:0.1, transition:"opacity 1.5s ease"}}>
          <defs>
            <pattern id="grid" width="56" height="56" patternUnits="userSpaceOnUse">
              <path d="M 56 0 L 0 0 0 56" fill="none" stroke="#8b7355" strokeWidth="0.4"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)"/>
        </svg>

        {TREES_DEFAULT.map((tr,i) => (
          <div key={`dt${i}`} className="tree-rise" style={{
            position:"absolute", left:`${tr.x}%`, bottom:`${100-tr.y}%`,
            transform:"translateX(-50%)", animationDelay:`${0.3+i*0.1}s`,
          }}>
            <Tree
              scale={isMobile ? tr.scale*0.65 : tr.scale}
              opacity={submitted ? Math.min(tr.opacity*2.0,1) : tr.opacity}
              color={submitted ? VIVID_COLORS[i%VIVID_COLORS.length] : MUTED_COLORS[i%MUTED_COLORS.length]}
              vivid={submitted}
            />
          </div>
        ))}

        {TREES_EXTRA.map((tr,i) => (
          <div key={`et${i}`} style={{
            position:"absolute", left:`${tr.x}%`, bottom:`${100-tr.y}%`,
            transform:"translateX(-50%)", opacity:submitted?1:0,
            animation:submitted?"extraTreeIn 0.9s cubic-bezier(0.16,1,0.3,1) forwards":"none",
            animationDelay:`${0.1+i*0.07}s`,
          }}>
            <Tree
              scale={isMobile ? tr.scale*0.65 : tr.scale}
              opacity={tr.opacity*1.8}
              color={VIVID_COLORS[(i+3)%VIVID_COLORS.length]}
              vivid={true} isExtra={true} extraDelay={0.1+i*0.07}
            />
          </div>
        ))}
      </div>

      {/* ── BUTTERFLIES ── */}
      <div style={{
        position:"absolute", inset:0, pointerEvents:"none",
        transition:"transform 0.3s cubic-bezier(0.25,0.46,0.45,0.94)",
        transform:`translate(${bx}px,${by}px)`,
      }}>
        {BUTTERFLIES_DEFAULT.map((b,i) => <Butterfly key={`db${i}`} {...b} vivid={submitted}/>)}
        {submitted && BUTTERFLIES_EXTRA.map((b,i) => <Butterfly key={`eb${i}`} {...b} vivid={true} extraDelay={0.15+i*0.12}/>)}
      </div>

      {/* Vignette */}
      <div style={{
        position:"absolute", inset:0, pointerEvents:"none", zIndex:3,
        background:submitted
          ?"radial-gradient(ellipse 90% 90% at 50% 50%, rgba(232,245,225,0) 10%, rgba(232,245,225,0.4) 60%, rgba(220,240,210,0.9) 100%)"
          :"radial-gradient(ellipse 90% 90% at 50% 50%, rgba(245,240,232,0) 10%, rgba(245,240,232,0.55) 60%, rgba(245,240,232,0.97) 100%)",
        transition:"background 1.6s ease",
      }}/>

      {/* Ground */}
      <div style={{
        position:"absolute", bottom:0, left:0, right:0, height:"36%",
        pointerEvents:"none", zIndex:2,
        background:submitted
          ?"linear-gradient(to top, rgba(100,200,80,0.2) 0%, transparent 100%)"
          :"linear-gradient(to top, rgba(210,195,165,0.18) 0%, transparent 100%)",
        transition:"background 1.8s ease",
      }}/>

      {/* ── MAIN CONTENT ── */}
      <div style={{
        position:"relative", zIndex:10,
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
        minHeight:"100vh",
        padding: isMobile ? "80px 28px 56px" : "60px 24px",
        textAlign:"center",
      }}>

        {/* Cities */}
        <div
          className="label fade-in"
          style={{
            animationDelay:"0.2s",
            marginBottom: isMobile ? 28 : 52,
            color:submitted?"rgba(30,100,50,0.7)":"rgba(100,80,55,0.5)",
            fontSize: isMobile ? 11 : 10,
            lineHeight: isMobile ? 2 : 1,
          }}
        >
          {isMobile ? (
            <>{t.citiesMobile}<br/><span style={{opacity:0.6,fontStyle:"italic"}}>{t.moreCity}</span></>
          ) : t.cities}
        </div>

        {/* Title */}
        <h1 style={{
          fontFamily:"'Cormorant Garamond',serif",
          fontWeight:300, fontStyle:"italic",
          fontSize: isMobile ? "clamp(56px, 17vw, 76px)" : "clamp(76px,15vw,172px)",
          lineHeight:0.92, letterSpacing:"-0.02em",
          color: submitted ? "#1a6b35" : "#2a1f12",
          transition:"color 1.4s ease",
          animation: submitted
            ? "titlePop 0.6s ease forwards"
            : "fadeUp 1s cubic-bezier(0.16,1,0.3,1) 0.35s forwards",
          opacity: submitted ? 1 : 0,
        }}>
          lets.bench.it
        </h1>

        {/* Divider */}
        <div style={{
          width:32, height:1,
          background:submitted?"rgba(30,140,60,0.4)":"rgba(100,80,55,0.28)",
          margin: isMobile ? "20px auto" : "30px auto",
          transition:"background 1.2s ease",
          animation:"fadeIn 1.2s ease 0.6s forwards", opacity:0,
        }}/>

        {/* Tagline */}
        <p style={{
          fontFamily:"'Cormorant Garamond',serif", fontWeight:300,
          fontSize: isMobile ? 21 : "clamp(17px,2.2vw,24px)",
          lineHeight:1.7,
          color: submitted?"rgba(20,80,40,0.75)":"rgba(60,45,28,0.65)",
          maxWidth: isMobile ? 290 : 340,
          marginBottom: isMobile ? 38 : 48,
          transition:"color 1.2s ease",
          animation:"fadeUp 1s cubic-bezier(0.16,1,0.3,1) 0.72s forwards",
          opacity: submitted ? 1 : 0,
        }}>
          {submitted ? t.taglineAfter : t.tagline}
        </p>

        {/* Form / Confirm */}
        <div style={{
          width:"100%", maxWidth: isMobile ? 340 : 290,
          display:"flex", flexDirection:"column", alignItems:"center",
          animation:"fadeUp 1s cubic-bezier(0.16,1,0.3,1) 0.9s forwards", opacity:0,
        }}>
          {submitted ? (
            <p style={{
              fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
              fontSize: isMobile ? 26 : 22, color:"#2a1f12", padding:"10px 0",
            }}>
              {t.confirm}
            </p>
          ) : (
            <>
              <div style={{marginBottom:16, width:"100%"}}>
                <input
                  type="text" placeholder={t.cityPlaceholder} value={city}
                  onChange={e => setCity(e.target.value)}
                  autoComplete="address-level2"
                />
              </div>
              <div style={{marginBottom:20, width:"100%"}}>
                <input
                  type="email" placeholder={t.placeholder} value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key==="Enter" && handleSubmit()}
                  autoComplete="email" inputMode="email"
                />
              </div>
              <button
                onMouseEnter={() => setBtnHover(true)}
                onMouseLeave={() => setBtnHover(false)}
                onClick={handleSubmit}
                style={{
                  fontFamily:"'Geist',sans-serif",
                  fontSize: isMobile ? 13 : 11,
                  fontWeight:400, letterSpacing:"0.13em", textTransform:"uppercase",
                  color:"#f5f0e8",
                  background: btnHover ? "#6b5335" : "#3d2f1e",
                  border:"none", cursor:"pointer",
                  padding: isMobile ? "0 28px" : "10px 20px",
                  height: isMobile ? 52 : "auto",
                  width: isMobile ? "100%" : "auto",
                  transition:"background 0.25s ease",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  gap:10, margin:"0 auto",
                  WebkitTapHighlightColor:"transparent",
                  borderRadius:2,
                }}
              >
                <BenchSVG size={16} color="#f5f0e8" wiggle={btnHover}/>
                {t.button}
              </button>
            </>
          )}
        </div>

        {/* iOS pill */}
        <div style={{marginTop:20, animation:"fadeUp 1s cubic-bezier(0.16,1,0.3,1) 1.05s forwards", opacity:0}}>
          <span
            className={`ios-pill${submitted?" vivid":""}`}
            style={{fontSize: isMobile ? 12 : 10, padding: isMobile ? "10px 20px" : "7px 16px"}}
          >
            <AppleIcon size={isMobile ? 15 : 13} color={submitted?"rgba(30,100,50,0.5)":"rgba(60,45,28,0.4)"}/>
            {t.ios}
          </span>
        </div>

        {/* Counter */}
        <div
          className="label fade-in"
          style={{
            animationDelay:"1.1s",
            marginTop: isMobile ? 28 : 36,
            color: submitted?"rgba(20,80,40,0.7)":"rgba(80,60,35,0.6)",
            display:"flex", alignItems:"baseline", gap:7, justifyContent:"center",
            fontSize: isMobile ? 11 : 10,
          }}
        >
          <span style={{
            fontFamily:"'Cormorant Garamond',serif",
            fontWeight:300, fontStyle:"italic",
            fontSize: isMobile ? 17 : 15,
            color: submitted?"rgba(20,80,40,0.85)":"rgba(80,60,35,0.75)",
            transition:"color 1.2s ease",
          }}>
            {fmt(count)}
          </span>
          {t.stat}
        </div>
      </div>

      <Analytics/>
    </div>
  );
}
